import Link from 'next/link';
import { Slider } from '@/ui/common/Slider/Slider';
import { TActivity } from '@/domain/Activities.domain';
import { numberWithCommaFormatter } from '@/service/utils/numberWithCommaFormatter';
import { useAttractionsStore } from '@/service/state/Attractions/useAttractionsStore';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { AvailabilityThumb } from './AvailabilityThumb';
import { createImagesSet } from '@/service/creators/createImageUrl';
import { Description } from './Description';
import { RatingStars } from './RatingStars';
import { trackSelectItem } from '@/service/GA4/ga4Events';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { CARDS_IMAGE_SIZES } from '@/service/constants/variables';
import { cn } from '@/service/utils/cn';

type TActivityCardProps = {
    activity: TActivity;
    partnerSlug: string;
    indexInList: number;
    price?: number;
    showReviews?: boolean;
};

export const ActivityCard = ({
    activity: {
        id,
        name,
        duration,
        description_clean,
        statuses,
        days_availability,
        rate,
        rate_count,
    },
    partnerSlug,
    indexInList,
    price,
    showReviews,
}: TActivityCardProps) => {
    const href = `/${partnerSlug}/details/${id}`;
    /* TODO add rate count and rating stars */
    const reviewsCount = +rate_count;
    const reviewsCountWithComma = numberWithCommaFormatter(reviewsCount);
    /* TODO add strike out price */
    //const price_strike_out = 0;

    const additionalImages = useAttractionsStore((s) => s.images)[`parent_${id}`];
    const images = !!additionalImages && additionalImages.length ? additionalImages : [];
    const slides = createImagesSet([{ id }, ...images], CARDS_IMAGE_SIZES);

    const { title, texts } = usePartnerStore();

    return (
        <Link
            href={href}
            passHref
            className="shadow-main flex h-[418px] w-full flex-col overflow-hidden rounded-2xl bg-white p-3 pb-5 text-gray-1 dark:bg-dark-3 dark:text-white md:h-[450px]"
            data-test-id="activity-card"
            onClick={() =>
                trackSelectItem({
                    items: [
                        {
                            item_id: id,
                            item_name: name,
                            affiliation: title,
                            item_brand: title,
                            item_category: 'Rentals', // TODO: ask to provide property
                            price: price || 0,
                            quantity: 1,
                            index: indexInList,
                        },
                    ],
                })
            }
            prefetch={false}
        >
            <div className="relative h-full min-h-[100px]">
                {!!price && (
                    <div
                        data-test-id="activity-card-price"
                        className="tx-heading-3 absolute left-3 top-3 z-[1] flex h-11 min-w-[90px] flex-col items-center justify-center rounded-3xl bg-white px-2 text-gray-1"
                    >
                        <span className="tx-normal-medium mx-2 leading-[8px] text-gray-2">
                            {texts?.['Starting at'] || 'from'}
                        </span>
                        {/* {!!price_strike_out && (
                            <div
                                data-test-id="strike-out-price"
                                className="tx-large-regular line-through"
                            >
                                {getFormattedPrice(+price_strike_out)}&nbsp;
                            </div>
                        )} */}
                        {getFormattedPrice(price)}
                    </div>
                )}
                <div className="relative h-full min-h-[100px] overflow-hidden rounded-2xl">
                    <Slider slides={slides}>
                        <div
                            className={cn(
                                'tx-large-regular mt-2 flex w-full text-gray-3 dark:text-gray-3',
                                showReviews && !!reviewsCount ? 'justify-between' : 'justify-end'
                            )}
                        >
                            {showReviews && !!reviewsCount && (
                                <div className="flex items-center" data-test-id="card-reviews">
                                    <RatingStars
                                        rating={rate}
                                        data-test-id="Rating_start"
                                        count={rate_count}
                                    />
                                    <span className="ml-2">
                                        {`${reviewsCountWithComma} ${
                                            reviewsCount > 1 || reviewsCount < 1
                                                ? 'ratings'
                                                : 'rating'
                                        }`}
                                    </span>
                                </div>
                            )}
                            {/* {showReviews && reviewsCount === 0 && (
                                <span className="text-sand-dark">Be the first to review!</span>
                            )} */}
                            {!!duration && (
                                <span
                                    className="flex items-center whitespace-nowrap"
                                    data-test-id="card-duration"
                                >
                                    <img src="/icon/HourWhite.svg" alt="Duration" />
                                    {duration}
                                </span>
                            )}
                        </div>
                    </Slider>
                </div>
                <AvailabilityThumb daysAvailability={days_availability} />
            </div>
            <Description description={description_clean} status={statuses.general?.[0]}>
                <h2 data-test-id="card-title" className="tx-large-semibold">
                    {name}
                </h2>
            </Description>
        </Link>
    );
};
